<script>
  import { onMount } from 'svelte';
  import { canadaTaxConfigForXeroBill, provinceBillTaxName, normalizeProvince, splitXeroLineTax } from '../lib/canadaTax.js';
  import Layout from '../components/Layout.svelte';
  import { navigate, useLocation } from 'svelte-routing';
  import { getJobValueFromLineItems, getPOValueFromLineItems, getAllPOValuesFromLineItems, jobMatchesTrackingValue } from '../lib/billMatching.js';
  import { normalizeForComparison, stripAllWhitespace, findVendorCandidates, parseCreatedVendor, isRequiredCustomFieldResult, mergeRequiredCustomFields, customFieldValuesFromSelections, requiredCustomFieldsFilled } from '../lib/vendorMatch.js';
  import RequiredVendorCustomField from '../components/RequiredVendorCustomField.svelte';

  const location = useLocation();

  let invoiceTrackingSettings = null;

  let selectedInvoices = [];
  let currentInvoiceIndex = 0;
  let currentInvoice = null;
  let error = null;

  // JobTread integration variables
  let customers = [];
  let selectedCustomer = null;
  let vendorOrders = [];
  let selectedVendorOrder = null;
  let isVendorOrderDropdownOpen = false;
  let totalAssigned = 0;
  let lineItemsData = null;
  let allItemsAssigned = false;
  let assignedItems = [];
  let vendorBillCreated = false;
  let isAutoMatched = false;
  let isLoadingVendorOrders = false;
  let isCreatingVendorBill = false;
  let vendorOrdersNextPage = null;
  let matchedPOCount = { found: 0, total: 0 };  // Track how many POs were matched
  let multipleMatchedPOs = false;  // Flag for multiple matched POs
  
  // Vendor matching variables
  let showVendorMatching = false;
  let jtVendors = [];
  let filteredVendors = [];
  let vendorSearchQuery = "";
  let selectedVendorId = null;
  let isLoadingVendors = false;
  let isCreatingVendor = false;
  let autoMatchedVendor = false; // Track if vendor was automatically matched
  let isAutoMatchingVendor = false; // Track if currently searching for vendor match
  let autoMatchFailed = false; // Track if auto-matching failed to find a vendor
  let pendingVendorCandidates = [];
  let pendingVendorSourceName = '';
  let requiredVendorFields = [];
  let requiredVendorFieldValues = {};
  let requiredVendorError = '';
  let vendorAliases = {};
  let vendorAliasesLoaded = false;

  // New variables for handling multiple vendor bills
  let matchedVendorBills = [];  // Array to store multiple matched vendor bills
  let selectedVendorBillForAdd = null;  // Selected vendor bill in the "Add Vendor Bill" dialog
  let showAddVendorBillDialog = false;  // Flag to show/hide the "Add Vendor Bill" dialog
  let lineItemVendorBillSelections = {};  // Map of lineItemID to selected vendor bill

  // Dynamic tax configuration based on user's country
  let taxConfig = {
    rate: 0.15,  // Default to NZ rate
    name: "GST", // Default to GST
    currency: "NZD", // Default to NZD
    locale: "en-NZ"  // Default to NZ locale
  };
  let userCountry = null;
  let userProvince = null;
  // Canada: each Xero bill carries its own rate (5% GST, 13% HST, 12% GST + PST
  // ...), so the tax config is set per bill from the bill's totals.
  let caBaseTaxConfig = null;
  // PST % per Xero tax type. Null until loaded (or for other countries), which
  // keeps the standard display. GST-only and HST lines stay on that display.
  let caNonRecoverableByTaxType = null;
  $: caLineSplits = (caNonRecoverableByTaxType && currentInvoice?.lineItems)
    ? Object.fromEntries(currentInvoice.lineItems.map(li => [li.lineItemID, splitXeroLineTax(li, currentInvoice.lineAmountTypes, caNonRecoverableByTaxType[li.taxType])]))
    : null;
  $: displayTotalAssigned = totalAssigned + (caLineSplits
    ? assignedItems.reduce((s, it) => s + (caLineSplits[it.invoiceLineItem?.lineItemID]?.nonRecoverable || 0), 0)
    : 0);

  // Variables for customer search autocomplete
  let customerSearchQuery = '';
  let showCustomerDropdown = false;
  let filteredCustomers = [];

  // Variables for vendor order search autocomplete
  let vendorOrderSearchQuery = '';
  let showVendorOrderDropdown = false;
  let filteredVendorOrders = [];

  // New variables for tracking multiple vendor bill creation
  let processingVendorBills = false;
  let vendorBillsProcessed = 0;
  let totalVendorBillsToProcess = 0;
  let vendorBillsCreationResults = [];

  // New variables for error handling
  let creationError = null;
  let showErrorModal = false;

  let currentVendorBillIndex = 0;
  let processingComplete = false;
  let processingError = null;
  let jobtreadConfig = null;
  
  // JobTread user/organization info for document creation
  let jobtreadUserName = "BuildZilla";  // Default fallback
  let jobtreadOrgName = "BuildZilla";  // Default fallback

  /**
   * Fetch JobTread user and organization info for document creation
   */
  async function fetchJobTreadUserInfo() {
    try {
      const response = await fetch('/api/jt/user-info');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          jobtreadUserName = data.userName || "BuildZilla";
          jobtreadOrgName = data.organizationName || "BuildZilla";
          console.log('📋 JobTread user info loaded:', { user: jobtreadUserName, org: jobtreadOrgName });
        }
      }
    } catch (error) {
      console.error('Error fetching JobTread user info:', error);
      // Keep default values
    }
  }

  onMount(async () => {
    // Fetch user data and set tax configuration first
    try {
      const userResponse = await fetch('/api/user');
      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.success && userData.user) {
          userCountry = userData.user.country;
          userProvince = userCountry === 'CA' ? normalizeProvince(userData.user.province) : null;
          
          // Set tax configuration based on country
          switch (userCountry) {
            case 'NZ':
              taxConfig = { rate: 0.15, name: "GST", currency: "NZD", locale: "en-NZ" };
              break;
            case 'AU':
              taxConfig = { rate: 0.10, name: "GST", currency: "AUD", locale: "en-AU" };
              break;
            case 'US':
              taxConfig = { rate: 0.00, name: "TAX", currency: "USD", locale: "en-US" };
              break;
            case 'GB':
              taxConfig = { rate: 0.00, name: "VAT", currency: "GBP", locale: "en-GB" };
              break;
            case 'GI':
              taxConfig = { rate: 0.00, name: "VAT", currency: "GIP", locale: "en-GI" };
              break;
            case 'CA':
              // Fallback only - replaced per bill from the bill's own tax (see loadCurrentInvoice).
              taxConfig = { rate: 0.05, name: userProvince ? provinceBillTaxName(userProvince) : "GST/HST", currency: "CAD", locale: "en-CA" };
              caBaseTaxConfig = taxConfig;
              fetch('/api/xero/non-recoverable-rates')
                .then(r => (r.ok ? r.json() : null))
                .then(d => { if (d?.nonRecoverableByTaxType) caNonRecoverableByTaxType = d.nonRecoverableByTaxType; })
                .catch(() => {});
              break;
            default:
              taxConfig = { rate: 0.15, name: "GST", currency: "NZD", locale: "en-NZ" }; // Default to NZ
          }
          
          invoiceTrackingSettings = userData.user.invoiceTrackingSettings || null;
          console.log('User country:', userCountry, 'Tax config:', taxConfig);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Use default tax config if fetch fails
    }

    // Fetch JobTread user/org info for personalized document creation
    await fetchJobTreadUserInfo();

    // Fetch customers (jobs) first - required for line-item matching
    try {
      await fetchJobs();
    } catch (error) {
      console.error('Error fetching customers:', error);
    }

    console.log('Location state:', $location.state);
    if ($location.state && $location.state.selectedInvoices) {
      // Reset all state variables to ensure fresh data
      selectedInvoices = $location.state.selectedInvoices;
      currentInvoiceIndex = 0;
      currentInvoice = null;
      error = null;
      selectedCustomer = null;
      selectedVendorOrder = null;
      vendorOrders = [];
      lineItemsData = null;
      allItemsAssigned = false;
      isAutoMatched = false;
      vendorBillCreated = false;
      assignedItems = [];
      showVendorMatching = false;
      jtVendors = [];
      filteredVendors = [];
      vendorSearchQuery = "";
      selectedVendorId = null;
      
      console.log('Received invoices:', selectedInvoices);
      loadCurrentInvoice();
    } else {
      error = 'No invoices selected';
      console.error('No selected invoices found in location state');
    }

    try {
      const response = await fetch('/api/jt/check-auth');
      const data = await response.json();
      console.log('JobTread auth check response:', data);
      
      // SECURITY: Server no longer sends grantKey - only hasGrantKey boolean
      if (data.authenticated && data.hasGrantKey && data.orgId) {
        jobtreadConfig = {
          orgId: data.orgId,
          authenticated: true
        };
        console.log('JobTread config set successfully:', { 
          authenticated: jobtreadConfig.authenticated, 
          hasOrgId: !!jobtreadConfig.orgId 
        });
      } else {
        console.error('Invalid auth response:', data);
        throw new Error('Not authenticated with JobTread or missing credentials');
      }
    } catch (error) {
      console.error('Error fetching JobTread config:', error);
      processingError = 'Failed to fetch JobTread configuration. Please refresh the page and try again.';
    }
  });

  async function loadCurrentInvoice() {
    if (currentInvoiceIndex < selectedInvoices.length) {
      currentInvoice = selectedInvoices[currentInvoiceIndex];
      if (userCountry === 'CA' && caBaseTaxConfig) {
        const own = canadaTaxConfigForXeroBill(currentInvoice, userProvince);
        taxConfig = own ? { ...caBaseTaxConfig, ...own } : caBaseTaxConfig;
      }
      console.log('Loaded invoice:', currentInvoice);
      
      // Reset JobTread selections
      selectedCustomer = null;
      selectedVendorOrder = null;
      vendorOrders = [];
      lineItemsData = null;
      allItemsAssigned = false;
      isAutoMatched = false;
      vendorBillCreated = false;
      assignedItems = [];
      multipleMatchedPOs = false;
      matchedPOCount = { found: 0, total: 0 };
      
      // Reset new multiple vendor bill variables
      matchedVendorBills = [];
      lineItemVendorBillSelections = {};

      // Simplified 2-step matching: Job first (advanced cat or Job Name), then PO (Jobtread PO Number only)
      if (currentInvoice.lineItems && currentInvoice.lineItems.length > 0 && customers.length > 0) {
        const jobValue = getJobValueFromLineItems(currentInvoice.lineItems, invoiceTrackingSettings, 'jtJobField');
        if (jobValue) {
          const matchingJob = customers.find(job => jobMatchesTrackingValue(job, jobValue));
          if (matchingJob) {
            console.log(`Auto-matched job from tracking '${jobValue}':`, matchingJob.name);
            isAutoMatched = true;
            selectedCustomer = matchingJob.id;
            customerSearchQuery = matchingJob.name;
            const jobEntry = { id: `job-${matchingJob.id}`, jobId: matchingJob.id, jobName: matchingJob.name };
            matchedVendorBills = [jobEntry];
            const allPOValues = getAllPOValuesFromLineItems(currentInvoice.lineItems);
            matchedPOCount.total = allPOValues.length || 1;
            matchedPOCount.found = 0;
            multipleMatchedPOs = allPOValues.length > 1;
            setTimeout(async () => {
              isLoadingVendorOrders = true;
              try {
                const response = await fetch(`/api/jt/documents?jtjobid=${selectedCustomer}`);
                const data = await response.json();
                vendorOrders = Array.isArray(data) ? data : (data.orders || []);
                const poValue = getPOValueFromLineItems(currentInvoice.lineItems);
                let matchingOrder = null;
                if (poValue && vendorOrders.length > 0) {
                  matchingOrder = vendorOrders.find(o =>
                    // Exact number match (most reliable)
                    String(o.number) === poValue ||
                    // Combined job-order format e.g. "HEWI01-495"
                    (o.job?.number && `${o.job.number}-${o.number}` === poValue) ||
                    // fullName ends with "-<poValue>" e.g. "Purchase Order HEWI01-495"
                    (o.fullName && o.fullName.endsWith(`-${poValue}`))
                  );
                  if (!matchingOrder && poValue.includes('-')) {
                    const numPart = poValue.split('-').pop();
                    matchingOrder = vendorOrders.find(o => String(o.number) === numPart);
                  }
                }
                if (matchingOrder && matchingOrder.number != null) {
                  selectedVendorOrder = matchingOrder.id;
                  matchedPOCount.found = 1;
                  const poBill = {
                    id: `${selectedCustomer}-${matchingOrder.id}`,
                    jobId: selectedCustomer,
                    jobName: matchingJob.name,
                    vendorOrderId: matchingOrder.id,
                    vendorOrderNumber: matchingOrder.number,
                    vendorName: matchingOrder.account?.name || 'Unknown',
                    poNumber: poValue,
                    lineItems: null
                  };
                  matchedVendorBills = [jobEntry, poBill];
                  await selectVendorOrder(selectedVendorOrder, false);
                  matchedVendorBills = matchedVendorBills.map(b => b.vendorOrderId === selectedVendorOrder ? { ...b, lineItems: lineItemsData } : b);
                }
                await autoAssignLineItems();
              } catch (err) {
                console.error('Error fetching vendor orders:', err);
              } finally {
                isLoadingVendorOrders = false;
              }
            }, 100);
          }
        }
      }
    } else {
      console.log('All invoices processed');
      navigate('/process-invoices');
    }
  }

  function nextInvoice() {
    currentInvoiceIndex++;
    loadCurrentInvoice();
  }

  function selectPercentage(lineItem, percentage) {
    console.log(`Selected ${percentage}% for line item:`, lineItem);
    lineItem.selectedPercentage = percentage;
    calculateAssignedCost(lineItem);
  }

  function calculateAssignedCost(lineItem) {
    if (lineItem.selectedPercentage !== undefined && lineItem.unitAmount) {
      lineItem.assignedCost = (lineItem.selectedPercentage / 100) * lineItem.unitAmount;
    } else {
      lineItem.assignedCost = null;
    }
  }

  async function handleCustomerSelect(event) {
    selectedCustomer = event.target.value;
    selectedVendorOrder = null; // Reset vendor order selection
    lineItemsData = null; // Reset line items data
    isAutoMatched = false; // Reset auto-match status since user manually selected
    vendorOrders = []; // Reset vendor orders array
    vendorOrdersNextPage = null; // Reset pagination token
    
    if (selectedCustomer) {
      isLoadingVendorOrders = true;
      try {
        let nextPage = null;
        let allOrders = [];
        
        do {
          // Fetch vendor orders with pagination
          const url = `/api/jt/documents?jtjobid=${selectedCustomer}${nextPage ? `&page=${nextPage}` : ''}`;
          console.log('Fetching vendor orders from:', url);
          
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error('Failed to fetch vendor orders');
          }
          
          const data = await response.json();
          console.log('Vendor orders page response:', data);
          
          // Check if we have documents in the response
          if (Array.isArray(data)) {
            allOrders = [...allOrders, ...data];
            // If data is an array, assume no more pages
            nextPage = null;
          } else if (data.orders && Array.isArray(data.orders)) {
            // Handle paginated response format
            allOrders = [...allOrders, ...data.orders];
            nextPage = data.nextPage;
          } else {
            console.error('Unexpected response format:', data);
            break;
          }
          
          console.log(`Fetched ${allOrders.length} orders so far, next page: ${nextPage}`);
        } while (nextPage);
        
        vendorOrders = allOrders;
        console.log('All vendor orders fetched:', vendorOrders.length);
        
      } catch (error) {
        console.error('Error fetching vendor orders:', error);
      } finally {
        isLoadingVendorOrders = false;
      }
    }
  }

  function toggleVendorOrderDropdown() {
    isVendorOrderDropdownOpen = !isVendorOrderDropdownOpen;
    showVendorOrderDropdown = isVendorOrderDropdownOpen;
    if (showVendorOrderDropdown) {
      filteredVendorOrders = vendorOrders;
    }
  }

  // Add functions to handle dropdown focus management
  function handleInputFocusOut(type) {
    // Use a small delay to allow click events to process first
    setTimeout(() => {
      if (type === 'customer') {
        showCustomerDropdown = false;
      } else if (type === 'vendorOrder') {
        showVendorOrderDropdown = false;
      }
    }, 200);
  }

  async function selectVendorOrder(orderId, toggleDropdown = true) {
    console.log('Selecting vendor order:', orderId);
    selectedVendorOrder = orderId;
    if (toggleDropdown) {
      isVendorOrderDropdownOpen = false;
    }
    
    try {
        const response = await fetch(`/api/jt/lineitems?jtjobid=${orderId}`);
        if (!response.ok) {
            throw new Error('Failed to fetch line items');
        }
        lineItemsData = await response.json();
        console.log('Fetched lineItemsData:', lineItemsData);
        
        // Verify the data structure
        if (!lineItemsData || !lineItemsData.costGroups) {
            console.error('Invalid lineItemsData structure:', lineItemsData);
            throw new Error('Invalid line items data received');
        }
    } catch (error) {
        console.error('Error fetching line items:', error);
        lineItemsData = null; // Reset on error
        
    }
  }

  function handleAssignmentSelect(lineItem, event) {
    console.log('handleAssignmentSelect called with:', { lineItem, eventValue: event.target.value });
    
    if (!event.target.value) {
        console.log('No value selected');
        return;
    }

    const [groupIndex, lineItemId] = event.target.value.split('|');
    
    // Get the selected vendor bill for this line item
    const selectedBillId = lineItemVendorBillSelections[lineItem.lineItemID];
    if (!selectedBillId) {
        console.error('No vendor bill selected for this line item');
        return;
    }
    
    // Find the selected vendor bill
    const selectedBill = matchedVendorBills.find(bill => bill.id === selectedBillId);
    if (!selectedBill || !selectedBill.lineItems) {
        console.error('Selected vendor bill not found or has no line items');
        return;
    }
    
    console.log('DEBUG: Selected vendor bill and line items:', {
        billId: selectedBillId,
        vendorOrderNumber: selectedBill.vendorOrderNumber,
        hasLineItems: !!selectedBill.lineItems,
        costGroupsCount: selectedBill.lineItems?.costGroups?.length || 0
    });
    
    // Get the line items data from the selected vendor bill
    const selectedBillLineItemsData = selectedBill.lineItems;
    
    // Make sure the selected bill's line items data is valid
    if (!selectedBillLineItemsData || !selectedBillLineItemsData.costGroups) {
        console.error('Invalid line items data structure for selected vendor bill:', selectedBillLineItemsData);
        return;
    }
    
    // Get the selected cost group from the selected bill's line items
    const selectedGroup = selectedBillLineItemsData.costGroups[groupIndex];
    if (!selectedGroup) {
        console.error('Selected cost group not found in vendor bill:', { groupIndex, billId: selectedBillId });
        return;
    }
    
    // Find the specific line item in the selected bill
    const originalItem = selectedGroup.lineItems.find(item => item.id === lineItemId);
    if (!originalItem) {
        console.error('Could not find line item in selected vendor bill:', { lineItemId, billId: selectedBillId });
        return;
    }

    // Calculate total amount (quantity × unit amount)
    const totalAmount = (lineItem.quantity || 1) * lineItem.unitAmount;

    // Create the new assigned item
    const newAssignedItem = {
        invoiceLineItem: {
            ...lineItem,
            assignedCost: totalAmount || 0
        },
        originalItem: originalItem,
        vendorBillId: selectedBillId // Store the vendor bill ID for this assignment
    };

    // Find if this line item is already assigned
    const existingIndex = assignedItems.findIndex(item => 
        item.invoiceLineItem.lineItemID === lineItem.lineItemID
    );

    if (existingIndex !== -1) {
        // Replace existing assignment
        assignedItems = [
            ...assignedItems.slice(0, existingIndex),
            newAssignedItem,
            ...assignedItems.slice(existingIndex + 1)
        ];
    } else {
        // Add new assignment
        assignedItems = [...assignedItems, newAssignedItem];
    }

    console.log('Updated assignedItems:', assignedItems);
    calculateTotalAssigned();
    checkAllItemsAssigned();
  }

  function calculateTotalAssigned() {
    console.log('Calculating total from assignedItems:', assignedItems);
    
    totalAssigned = assignedItems.reduce((sum, item) => {
      const amount = item.invoiceLineItem?.assignedCost || 0;
      console.log(`Adding amount: ${amount} from line item: ${item.invoiceLineItem.description} assigned to vendor bill: ${item.vendorBillId}`);
      return sum + amount;
    }, 0);
    
    console.log('New total assigned:', totalAssigned);
  }

  function checkAllItemsAssigned() {
    calculateTotalAssigned();
    
    // Check if we have assignments for all line items
    allItemsAssigned = assignedItems.length === currentInvoice.lineItems.length;
    
    console.log('Checking all items assigned:', {
        assignedItemsLength: assignedItems.length,
        lineItemsLength: currentInvoice.lineItems.length,
        allItemsAssigned: allItemsAssigned
    });
  }

  async function confirmAndPrepareVendorBill() {
    if (!assignedItems?.length) {
      console.error('No assigned items available');
      return;
    }

    // Reset vendor-related state to ensure fresh data
    jtVendors = [];
    filteredVendors = [];
    vendorSearchQuery = "";
    selectedVendorId = null;
    autoMatchedVendor = false;
    autoMatchFailed = false;
    
    // Show vendor matching interface immediately
    showVendorMatching = true;
    
    // Start showing the searching message immediately
    isAutoMatchingVendor = true;
    
    // Force a fresh vendor search
    await searchVendors();
    
    // Try to automatically match vendor from the selected vendor bills
    await tryAutoMatchVendor();
    
    // Scroll to vendor matching section after a short delay to ensure it's rendered
    setTimeout(() => {
      const vendorMatchingSection = document.getElementById('vendor-matching-section');
      if (vendorMatchingSection) {
        vendorMatchingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }

  async function searchVendors(query = "") {
    isLoadingVendors = true;
    vendorSearchQuery = query;
    
    try {
      // Add cache-busting timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await fetch(`/api/jt/vendors?t=${timestamp}`);
      if (!response.ok) {
        throw new Error('Failed to fetch vendors');
      }
      
      jtVendors = await response.json();
      console.log('Fetched vendors:', jtVendors);
      
      // Filter vendors based on search query
      filterVendors();
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      isLoadingVendors = false;
    }
  }

  // normalizeForComparison, stripAllWhitespace, and findVendorCandidates
  // are imported from ../lib/vendorMatch.js

  async function loadVendorAliases() {
    if (vendorAliasesLoaded) return;
    try {
      const response = await fetch('/api/users/vendor-aliases');
      if (response.ok) {
        const data = await response.json();
        vendorAliases = data.vendorAliases || {};
      }
    } catch (err) {
      console.warn('Could not load vendor aliases:', err.message);
    }
    vendorAliasesLoaded = true;
  }

  async function confirmVendorCandidate(vendor, sourceName) {
    selectedVendorId = vendor.id;
    vendorSearchQuery = vendor.name;
    autoMatchedVendor = true;
    pendingVendorCandidates = [];
    filterVendors();

    try {
      await fetch('/api/users/vendor-aliases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceName, jtVendorId: vendor.id })
      });
      const key = normalizeForComparison(sourceName);
      vendorAliases[key] = vendor.id;
      console.log(`💾 Saved vendor alias: "${sourceName}" -> "${vendor.name}"`);
    } catch (err) {
      console.warn('Failed to save vendor alias:', err.message);
    }

    await createVendorBill();
    showVendorMatching = false;
  }

  function rejectVendorCandidates() {
    pendingVendorCandidates = [];
    autoMatchFailed = true;
  }

  async function tryAutoMatchVendor() {
    autoMatchedVendor = false;
    autoMatchFailed = false;
    pendingVendorCandidates = [];

    if (matchedVendorBills.length === 0 || jtVendors.length === 0) {
      autoMatchFailed = true;
      isAutoMatchingVendor = false;
      return;
    }

    await loadVendorAliases();
    await new Promise(resolve => setTimeout(resolve, 500));

    const vendorNamesFromPOs = [...new Set(matchedVendorBills.map(bill => bill.vendorName).filter(name => name && name !== 'Unknown Vendor'))];
    console.log('Vendor names from POs:', vendorNamesFromPOs);

    for (const vendorName of vendorNamesFromPOs) {
      const { exactMatch, candidates } = findVendorCandidates(vendorName, jtVendors, vendorAliases);

      if (exactMatch) {
        console.log(`✅ Auto-matched: "${vendorName}" -> "${exactMatch.name}"`);
        selectedVendorId = exactMatch.id;
        autoMatchedVendor = true;
        vendorSearchQuery = exactMatch.name;
        filterVendors();
        break;
      }

      if (candidates.length > 0) {
        pendingVendorCandidates = candidates;
        pendingVendorSourceName = vendorName;
        console.log(`🔍 Found ${candidates.length} candidate(s) for "${vendorName}":`, candidates.map(v => v.name));
        break;
      }
    }

    if (!autoMatchedVendor && pendingVendorCandidates.length === 0) {
      console.log('❌ No automatic vendor match found');
      autoMatchFailed = true;
    }

    isAutoMatchingVendor = false;
  }

  function filterVendors() {
    if (!vendorSearchQuery || vendorSearchQuery.trim() === "") {
      filteredVendors = jtVendors;
    } else {
      const query = vendorSearchQuery.toLowerCase().trim();
      filteredVendors = jtVendors.filter(vendor => 
        vendor.name.toLowerCase().includes(query)
      );
    }
  }

  function handleVendorSearch(event) {
    vendorSearchQuery = event.target.value;
    // Reset auto-match status when user manually searches
    if (autoMatchedVendor || autoMatchFailed) {
      autoMatchedVendor = false;
      autoMatchFailed = false;
    }
    filterVendors();
  }

  function selectVendor(vendorId) {
    selectedVendorId = vendorId;
  }

  async function createNewVendor() {
    if (!currentInvoice?.contact?.name) {
      alert('No vendor information available from Xero');
      return;
    }
    if (!requiredCustomFieldsFilled(requiredVendorFields, requiredVendorFieldValues)) {
      requiredVendorError = requiredVendorError || 'Choose the required JobTread field, then create the vendor.';
      return;
    }

    isCreatingVendor = true;

    try {
      const vendorData = {
        name: currentInvoice.contact.name
      };

      if (currentInvoice.contact.emailAddress) {
        vendorData.email = currentInvoice.contact.emailAddress;
      }

      if (currentInvoice.contact.phoneNumber) {
        vendorData.phone = currentInvoice.contact.phoneNumber;
      }

      const customFieldValues = customFieldValuesFromSelections(requiredVendorFieldValues);
      if (Object.keys(customFieldValues).length) {
        vendorData.customFieldValues = customFieldValues;
      }

      const response = await fetch('/api/jt/createvendor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vendorData)
      });

      const result = await response.json().catch(() => ({}));
      if (isRequiredCustomFieldResult(result)) {
        requiredVendorFields = mergeRequiredCustomFields(requiredVendorFields, result.requiredCustomFields);
        requiredVendorError = result.error;
        return;
      }
      if (!response.ok) {
        throw new Error(result.error || result.message || `HTTP error! status: ${response.status}`);
      }

      const created = parseCreatedVendor(result, vendorData.name);
      if (!created) {
        throw new Error('Failed to get vendor ID from response');
      }

      if (!jtVendors.some(v => v.id === created.id)) {
        jtVendors = [...jtVendors, created];
      }
      selectedVendorId = created.id;
      requiredVendorFields = [];
      requiredVendorFieldValues = {};
      requiredVendorError = '';
      createVendorBill();
    } catch (error) {
      console.error('Error creating vendor:', error);
      alert('Failed to create vendor: ' + error.message);
    } finally {
      isCreatingVendor = false;
    }
  }

  function cancelVendorMatching() {
    showVendorMatching = false;
    selectedVendorId = null;
    autoMatchedVendor = false;
    isAutoMatchingVendor = false;
    autoMatchFailed = false;
    pendingVendorCandidates = [];
    pendingVendorSourceName = '';
    requiredVendorFields = [];
    requiredVendorFieldValues = {};
    requiredVendorError = '';
  }

  // Add this function to close the error modal
  function closeErrorModal() {
    showErrorModal = false;
    creationError = null;
  }

  async function createVendorBill() {
    if (!assignedItems?.length) {
      console.error('No assigned items available');
      return;
    }

    isCreatingVendorBill = true;
    vendorBillCreated = false;
    processingVendorBills = true;
    vendorBillsProcessed = 0;
    vendorBillsCreationResults = [];
    creationError = null;
    showErrorModal = false;
    vendorSearchQuery = '';
    filterVendors();

    try {
      if (!selectedVendorId) {
        throw new Error('No vendor selected. Please select a vendor before creating vendor bills.');
      }

      const assignmentsByVendorBill = {};
      for (const item of assignedItems) {
        const vbId = item.vendorBillId;
        if (!vbId) continue;
        if (!assignmentsByVendorBill[vbId]) assignmentsByVendorBill[vbId] = [];
        assignmentsByVendorBill[vbId].push(item);
      }

      const billEntries = [];
      for (const [vendorBillId, vendorBillItems] of Object.entries(assignmentsByVendorBill)) {
        if (!vendorBillItems.length) continue;

        const bill = matchedVendorBills.find(b => b.id === vendorBillId);
        if (!bill) {
          vendorBillsCreationResults.push({ success: false, vendorBillId, error: 'Vendor bill not found' });
          vendorBillsProcessed++;
          continue;
        }

        const items = vendorBillItems.map(item => ({
          invoiceLineItem: {
            description: item.invoiceLineItem.description,
            quantity: item.invoiceLineItem.quantity,
            unitAmount: item.invoiceLineItem.unitAmount,
            taxType: item.invoiceLineItem.taxType,
            taxAmount: item.invoiceLineItem.taxAmount,
            // Xero's own line total, so the server takes a tax-inclusive line back to
            // pre-tax from the line's own tax instead of a flat rate.
            lineAmount: item.invoiceLineItem.lineAmount,
            gstTreatment: item.invoiceLineItem.gstTreatment
          },
          originalItem: {
            id: item.originalItem?.id,
            name: item.originalItem?.name,
            costTypeId: item.originalItem?.costTypeId,
            jobCostItemId: item.originalItem?.jobCostItemId || item.originalItem?.id
          },
          costGroupId: null,
          isSplitAssignment: false,
          hasGST: null
        }));

        billEntries.push({
          mode: 'po',
          jobId: bill.jobId,
          vendorOrderId: bill.vendorOrderId,
          vendorOrderNumber: bill.vendorOrderNumber,
          items
        });
      }

      totalVendorBillsToProcess = billEntries.length;

      const apiPayload = {
        vendorId: selectedVendorId,
        xeroInvoiceID: currentInvoice.invoiceID,
        billInfo: {
          invoiceNumber: currentInvoice.invoiceNumber,
          invoiceID: currentInvoice.invoiceID,
          contactName: currentInvoice.contact?.name,
          date: currentInvoice.date,
          dueDate: currentInvoice.dueDate,
          lineAmountTypes: currentInvoice.lineAmountTypes,
          total: currentInvoice.total
        },
        taxConfig: { rate: taxConfig.rate, name: taxConfig.name, nonRecoverableName: taxConfig.nonRecoverableName || null },
        billEntries
      };

      let response = await fetch('/api/jt/createvendorbill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload)
      });

      // JobTread already has a bill from this vendor with this invoice number:
      // ask before creating a second one.
      if (response.status === 409) {
        const dup = await response.clone().json().catch(() => ({}));
        if (dup.duplicateSupplierInvoice && window.confirm(`${dup.error}

OK = create it in JobTread anyway. Cancel = leave it.`)) {
          response = await fetch('/api/jt/createvendorbill', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...apiPayload, allowDuplicateSupplierInvoice: true })
          });
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.createDocument) {
        const doc = result.createDocument.createdDocument;
        if (doc) {
          vendorBillsCreationResults.push({
            success: true,
            vendorBillId: `${billEntries[0]?.jobId}-${billEntries[0]?.vendorOrderId}`,
            vendorOrderNumber: billEntries[0]?.vendorOrderNumber,
            documentId: doc.id, jobId: doc.job?.id,
            url: `https://app.jobtread.com/jobs/${doc.job?.id}/documents/${doc.id}`
          });
          if (!currentInvoice.createdDocument) {
            currentInvoice.createdDocument = { id: doc.id, jobId: doc.job?.id };
          }
        }
        vendorBillsProcessed++;
      } else if (result.results) {
        for (const r of result.results) {
          vendorBillsCreationResults.push(r);
          if (r.success && !currentInvoice.createdDocument) {
            currentInvoice.createdDocument = { id: r.documentId, jobId: r.jobId };
          }
          vendorBillsProcessed++;
        }
      }

      vendorBillCreated = vendorBillsCreationResults.some(r => r.success);

      const failed = vendorBillsCreationResults.find(r => !r.success);
      if (failed) {
        creationError = failed.error || 'An error occurred creating a vendor bill';
        showErrorModal = true;
      }

      console.log('Vendor bill creation completed:', {
        totalProcessed: vendorBillsProcessed,
        totalSuccessful: vendorBillsCreationResults.filter(r => r.success).length,
        results: vendorBillsCreationResults
      });

    } catch (error) {
      console.error('Error in createVendorBill:', error);
      creationError = error.message || 'An error occurred while preparing the vendor bills';
      showErrorModal = true;
    } finally {
      isCreatingVendorBill = false;
      processingVendorBills = false;
    }
  }

  // Helper function to handle GST calculations
  function handleGST(lineItem) {
    const amount = lineItem.unitAmount || 0;
    const quantity = lineItem.quantity || 1;
    const split = caLineSplits?.[lineItem.lineItemID];
    if (split?.nonRecoverable) {
      return {
        displayAmount: split.jobCost / quantity,
        jtAmount: amount,
        gstAmount: split.recoverable / quantity,
        isExclusive: currentInvoice?.lineAmountTypes === 'Exclusive'
      };
    }

    if (lineItem.gstTreatment === 'EXCLUSIVE' || lineItem.gstTreatment === 'NO_GST') {
      return {
        displayAmount: amount * (1 + taxConfig.rate), // Show amount including tax for display
        jtAmount: amount, // Keep original amount for JT (they'll add tax)
        gstAmount: amount * taxConfig.rate,
        isExclusive: true
      };
    } else {
      // Default to inclusive (including when gstTreatment is 'INCLUSIVE' or undefined)
      const exclusiveAmount = taxConfig.rate > 0 ? amount / (1 + taxConfig.rate) : amount;
      return {
        displayAmount: amount, // Keep original inclusive amount for display
        jtAmount: exclusiveAmount, // Send exclusive amount to JT
        gstAmount: amount - exclusiveAmount,
        isExclusive: false
      };
    }
  }

  // Helper function to get display amount (including GST for exclusive)
  function getDisplayAmount(lineItem) {
    return handleGST(lineItem).displayAmount;
  }

  // Helper function to get total display amount
  function getTotalDisplayAmount(lineItem) {
    return getDisplayAmount(lineItem) * (lineItem.quantity || 1);
  }

  // Helper function to get tax amount
  function getTaxAmount(lineItem) {
    return handleGST(lineItem).gstAmount;
  }

  // Build JobTread document URL
  function getJobTreadDocumentUrl() {
    if (currentInvoice?.createdDocument?.id) {
      const jobId = currentInvoice.createdDocument.jobId || "22NqLi4KW3js";
      const documentId = currentInvoice.createdDocument.id;
      return `https://app.jobtread.com/jobs/${jobId}/documents/${documentId}`;
    }
    return null;
  }

  async function fetchJobs() {
    try {
      let allJobs = [];
      let nextPage = null;
      
      do {
        const response = await fetch(`/api/jt/jobs${nextPage ? `?page=${nextPage}` : ''}`);
        const data = await response.json();
        
        if (data.jobs) {
          allJobs = [...allJobs, ...data.jobs];
        }
        
        nextPage = data.nextPage;
        console.log('Fetched jobs:', {
          currentCount: allJobs.length,
          hasNextPage: !!nextPage
        });
      } while (nextPage);
      
      customers = allJobs;
      console.log('Total jobs fetched:', customers.length);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }

  // New function to toggle the Add Vendor Bill dialog
  function toggleAddVendorBillDialog() {
    showAddVendorBillDialog = !showAddVendorBillDialog;
    
    // Clear form values when opening the dialog
    if (showAddVendorBillDialog) {
      customerSearchQuery = '';
      vendorOrderSearchQuery = '';
      selectedCustomer = null;
      selectedVendorOrder = null;
      showCustomerDropdown = false;
      showVendorOrderDropdown = false;
    }
  }
  
  // New function to add a vendor bill to the matched list
  async function addVendorBill() {
    if (!selectedCustomer || !selectedVendorOrder) {
      console.error('Cannot add vendor bill: Customer or Vendor Order not selected');
      return;
    }
    
    // Check if this bill is already in the list
    const existingBill = matchedVendorBills.find(bill => 
      bill.jobId === selectedCustomer && bill.vendorOrderId === selectedVendorOrder
    );
    
    if (existingBill) {
      console.log('This vendor bill is already in the list');
      return;
    }
    
    // Find the selected vendor order details
    const selectedOrder = vendorOrders.find(order => order.id === selectedVendorOrder);
    if (!selectedOrder) {
      console.error('Selected vendor order not found in list');
      return;
    }
    
    // Find the selected job details
    const selectedJob = customers.find(job => job.id === selectedCustomer);
    
    // Create a new vendor bill object
    const newBill = {
      id: `${selectedCustomer}-${selectedVendorOrder}`,
      jobId: selectedCustomer,
      jobName: selectedJob?.name || 'Unknown Job',
      vendorOrderId: selectedVendorOrder,
      vendorOrderNumber: selectedOrder.number || 'Unknown PO',
      vendorName: selectedOrder.account?.name || 'Unknown Vendor',
      lineItems: lineItemsData
    };
    
    matchedVendorBills = [...matchedVendorBills, newBill];
    console.log('Added new vendor bill to array:', newBill);
    
    // Close the dialog
    showAddVendorBillDialog = false;
  }
  
  // New function to select a vendor bill for a line item
  async function selectVendorBillForLineItem(lineItemId, vendorBillId) {
    lineItemVendorBillSelections = {
      ...lineItemVendorBillSelections,
      [lineItemId]: vendorBillId
    };
    console.log('Updated line item vendor bill selections:', lineItemVendorBillSelections);

    // If a vendor bill was selected (not just unselected)
    if (vendorBillId) {
      // Find the selected bill
      const selectedBill = matchedVendorBills.find(bill => bill.id === vendorBillId);
      
      // If the bill exists but doesn't have line items loaded yet, fetch them
      if (selectedBill && !selectedBill.lineItems) {
        console.log(`Fetching line items for vendor bill with ID ${selectedBill.vendorOrderId}`);
        
        try {
          const response = await fetch(`/api/jt/lineitems?jtjobid=${selectedBill.vendorOrderId}`);
          if (!response.ok) {
            throw new Error('Failed to fetch line items for the selected vendor bill');
          }
          
          const lineItemsData = await response.json();
          
          // Update the line items in the matched vendor bills array
          matchedVendorBills = matchedVendorBills.map(bill => {
            if (bill.id === vendorBillId) {
              return {
                ...bill,
                lineItems: lineItemsData
              };
            }
            return bill;
          });
          
          console.log(`Successfully loaded line items for vendor bill ${vendorBillId}`);
        } catch (error) {
          console.error('Error fetching line items for vendor bill:', error);
          alert('Failed to load line items for the selected vendor bill: ' + error.message);
        }
      }
    }
  }
  
  // New function to get the selected vendor bill for a line item
  function getSelectedVendorBillForLineItem(lineItemId) {
    return lineItemVendorBillSelections[lineItemId] || null;
  }
  
  // New function to remove a vendor bill from the matched list
  function removeVendorBill(vendorBillId) {
    matchedVendorBills = matchedVendorBills.filter(bill => bill.id !== vendorBillId);
    
    // Update line item selections to remove references to this bill
    for (const lineItemId in lineItemVendorBillSelections) {
      if (lineItemVendorBillSelections[lineItemId] === vendorBillId) {
        const newSelections = { ...lineItemVendorBillSelections };
        delete newSelections[lineItemId];
        lineItemVendorBillSelections = newSelections;
      }
    }
  }

  // New function to auto-assign line items to vendor bills
  // Uses the first PO found in any line item and applies it to ALL line items
  async function autoAssignLineItems() {
    console.log('Auto-assigning line items to vendor bills - using first PO match for all lines');
    
    if (!currentInvoice.lineItems || currentInvoice.lineItems.length === 0) return;

    // Get the first PO value from any line item (bill-level, not per-line)
    const poValue = getPOValueFromLineItems(currentInvoice.lineItems);
    
    // Find the matching vendor bill (one with vendorOrderNumber - the actual PO)
    let matchingBill = null;
    if (poValue && matchedVendorBills.length > 0) {
      matchingBill = matchedVendorBills.find(bill => 
        bill.poNumber === poValue || 
        String(bill.vendorOrderNumber) === poValue
      );
      if (!matchingBill && poValue.includes('-')) {
        const numPart = poValue.split('-').pop();
        matchingBill = matchedVendorBills.find(bill => String(bill.vendorOrderNumber) === numPart);
      }
      if (!matchingBill) {
        matchingBill = matchedVendorBills.find(bill => bill.vendorOrderNumber != null);
      }
    }
    if (!matchingBill && matchedVendorBills.length > 0) {
      matchingBill = matchedVendorBills.find(bill => bill.vendorOrderNumber != null) || matchedVendorBills[0];
    }

    if (!matchingBill) return;

    console.log(`Using vendor bill "${matchingBill.jobName} - PO #${matchingBill.vendorOrderNumber}" for all ${currentInvoice.lineItems.length} line items`);
    
    // Apply the same vendor bill to ALL line items
    const newSelections = { ...lineItemVendorBillSelections };
    for (const lineItem of currentInvoice.lineItems) {
      newSelections[lineItem.lineItemID] = matchingBill.id;
    }
    lineItemVendorBillSelections = newSelections;
    
    console.log('Completed auto-assignment of line items to vendor bills:', lineItemVendorBillSelections);
  }

  function handleCustomerSearch(event) {
    customerSearchQuery = event.target.value;
    filterCustomers();
  }
  
  function filterCustomers() {
    if (!customerSearchQuery || customerSearchQuery.trim() === '') {
      filteredCustomers = customers;
    } else {
      const query = customerSearchQuery.toLowerCase().trim();
      filteredCustomers = customers.filter(customer => 
        customer.name.toLowerCase().includes(query)
      );
    }
  }
  
  function selectFilteredCustomer(customerId) {
    selectedCustomer = customerId;
    const selectedCust = customers.find(c => c.id === customerId);
    if (selectedCust) {
      customerSearchQuery = selectedCust.name;
    }
    showCustomerDropdown = false;
    
    // Trigger the same logic as the original select handler
    handleCustomerSelect({ target: { value: customerId } });
  }

  function handleVendorOrderSearch(event) {
    vendorOrderSearchQuery = event.target.value;
    filterVendorOrders();
  }
  
  function filterVendorOrders() {
    if (!vendorOrderSearchQuery || vendorOrderSearchQuery.trim() === '') {
      filteredVendorOrders = vendorOrders;
    } else {
      const query = vendorOrderSearchQuery.toLowerCase().trim();
      filteredVendorOrders = vendorOrders.filter(order => 
        order.number.toString().toLowerCase().includes(query) ||
        (order.subject && order.subject.toLowerCase().includes(query))
      );
    }
  }
  
  function selectFilteredVendorOrder(orderId) {
    selectVendorOrder(orderId, true);
    const selectedOrder = vendorOrders.find(o => o.id === orderId);
    if (selectedOrder) {
      vendorOrderSearchQuery = `PO #${selectedOrder.number}`;
    }
    showVendorOrderDropdown = false;
  }

  // Add this function to show all vendor orders when clicking the field
  function showVendorOrdersList() {
    showVendorOrderDropdown = true;
    // Initialize with all vendor orders
    filteredVendorOrders = vendorOrders;
  }
</script>

<Layout activeRoute="process-invoices">
  <div class="process-selected-invoices text-sm">
    <h1 class="text-xl font-normal text-gray-900 mb-3">Process Selected Invoice</h1>
    
    {#if error}
      <p class="error">{error}</p>
    {:else if !currentInvoice}
      <p>No invoice data available.</p>
    {:else}
      <div class="grid grid-cols-2 gap-3">
        <div class="invoice-details">
          <h2 class="text-lg font-normal mb-1">Invoice {currentInvoice.invoiceNumber}</h2>
          <p>Vendor: {currentInvoice.contact?.name || 'N/A'}</p>
          <p>Date: {new Date(currentInvoice.date).toLocaleDateString()}</p>
          <p>Due Date: {new Date(currentInvoice.dueDate).toLocaleDateString()}</p>
          <p>Total: {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(currentInvoice.total)}</p>
          <p>Status: {currentInvoice.status}</p>
          <p>Type: {currentInvoice.type}</p>
        </div>

        <!-- Vendor Bills being Matched section -->
        <div class="matched-vendor-bills bg-white rounded-lg border border-gray-200 p-3">
          <div class="flex justify-between items-center mb-2">
            <h2 class="text-lg font-normal">Vendor Bills being Matched</h2>
            <button 
              on:click={toggleAddVendorBillDialog}
              class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm flex items-center"
            >
              <span class="mr-1">+</span> Add
            </button>
          </div>
          
          {#if isAutoMatched}
            <div class="mb-3 p-2 bg-green-100 border border-green-300 rounded-md flex items-center">
              <span class="text-green-700 mr-2">✓</span>
              {#if multipleMatchedPOs}
                <span>Multiple unique Jobtread PO numbers found.</span>
              {:else if matchedPOCount.total > 0}
                <span>{matchedPOCount.found}/{matchedPOCount.total} Jobtread PO numbers matched</span>
              {:else}
              <span>Pre-matched to PO based on Xero Reference</span>
              {/if}
            </div>
          {/if}
          
          {#if matchedVendorBills.length === 0}
            <p class="text-gray-500 italic">No PO matched yet. Add a PO using the "Add" button.</p>
          {:else}
            <div class="space-y-1 max-h-60 overflow-y-auto">
              {#each matchedVendorBills as bill}
                <div class="bg-gray-50 border border-gray-200 rounded p-2">
                  <div class="flex justify-between">
                    <div>
                      <p class="font-medium">{bill.jobName}</p>
                      <p>PO #{bill.vendorOrderNumber} - {bill.vendorName}</p>
                    </div>
                    <button 
                      on:click={() => removeVendorBill(bill.id)}
                      class="text-red-500 hover:text-red-700 text-xl font-bold border-0"
                      title="Remove this vendor bill"
                    >
                      ×
                    </button>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>

      <h3 class="text-lg font-normal mt-3 mb-2">Line Items</h3>
      {#if currentInvoice.lineItems && currentInvoice.lineItems.length > 0}
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Line Item</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Cost</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider w-16">Qty</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Total</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Job Name</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Jobtread PO</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Vendor Bill</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Assignment</th>
                <th class="px-3 py-2 border-b border-gray-200 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-400 uppercase tracking-wider">Assigned Cost</th>
              </tr>
            </thead>
            <tbody class="bg-white">
              {#each currentInvoice.lineItems as lineItem}
                <tr>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">{lineItem.description}</td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    {#if caLineSplits?.[lineItem.lineItemID]?.nonRecoverable}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(caLineSplits[lineItem.lineItemID].jobCost / (Number(lineItem.quantity) || 1))}
                    {:else if currentInvoice.lineAmountTypes === 'Exclusive'}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(lineItem.unitAmount * (1 + taxConfig.rate))}
                    {:else}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(lineItem.unitAmount)}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 w-16">{lineItem.quantity || 1}</td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    {#if caLineSplits?.[lineItem.lineItemID]?.nonRecoverable}
                      {@const split = caLineSplits[lineItem.lineItemID]}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(split.jobCost)}
                      <div class="text-[11px] text-gray-500">incl. PST {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(split.nonRecoverable)}{#if split.recoverable} · + {taxConfig.name} {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(split.recoverable)}{/if}</div>
                    {:else if currentInvoice.lineAmountTypes === 'Exclusive'}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(lineItem.lineAmount + lineItem.taxAmount)}
                    {:else}
                      {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(lineItem.lineAmount)}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 text-center">
                    {#if lineItem.tracking && lineItem.tracking.length > 0}
                      {@const jobVal = getJobValueFromLineItems([lineItem], invoiceTrackingSettings, 'jtJobField')}
                      {#if jobVal}
                        <span class="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">{jobVal}</span>
                      {/if}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 text-center">
                    {#if lineItem.tracking && lineItem.tracking.length > 0}
                      {@const poVal = getPOValueFromLineItems([lineItem])}
                      {#if poVal}
                        <span class="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">{poVal}</span>
                      {/if}
                    {/if}
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    <select 
                      class="w-full p-1 rounded text-sm {!lineItemVendorBillSelections[lineItem.lineItemID] ? 'border-2 border-orange-400 text-gray-500' : 'border border-gray-300'}" 
                      on:change={(event) => selectVendorBillForLineItem(lineItem.lineItemID, event.target.value)}
                      value={lineItemVendorBillSelections[lineItem.lineItemID] || ''}
                    >
                      <option value="">Select Vendor Bill</option>
                      {#each matchedVendorBills as bill}
                        <option value={bill.id}>
                          {bill.jobName} - PO #{bill.vendorOrderNumber}
                        </option>
                      {/each}
                    </select>
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100">
                    <select 
                      class="w-full p-1 rounded text-sm {(!lineItemVendorBillSelections[lineItem.lineItemID] || !assignedItems.some(item => item.invoiceLineItem.lineItemID === lineItem.lineItemID)) ? 'border-2 border-orange-400 text-gray-500' : 'border border-gray-300'}"
                      disabled={!lineItemVendorBillSelections[lineItem.lineItemID] || !matchedVendorBills.find(bill => bill.id === lineItemVendorBillSelections[lineItem.lineItemID])?.lineItems}
                      on:change={(event) => handleAssignmentSelect(lineItem, event)}
                    >
                      <option value="">Assigned a PO item</option>
                      {#if lineItemVendorBillSelections[lineItem.lineItemID]}
                        {@const selectedBill = matchedVendorBills.find(bill => bill.id === lineItemVendorBillSelections[lineItem.lineItemID])}
                        {#if selectedBill && selectedBill.lineItems?.costGroups}
                          {#each selectedBill.lineItems.costGroups as costGroup, index}
                            <optgroup label={costGroup.name || `Group ${index + 1}`}>
                                {#each costGroup.lineItems as item}
                                    <option value={`${index}|${item.id}`}>
                                        {item.name} - {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(item.cost)}
                                    </option>
                                {/each}
                            </optgroup>
                        {/each}
                        {/if}
                      {/if}
                    </select>
                  </td>
                  <td class="px-3 py-2 whitespace-no-wrap border-b border-gray-100 text-right">
                    {#if assignedItems.some(item => item.invoiceLineItem.lineItemID === lineItem.lineItemID)}
                      {@const assignedItem = assignedItems.find(item => item.invoiceLineItem.lineItemID === lineItem.lineItemID)}
                      <span class="text-green-600">
                        ✓ {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(
                          assignedItem ? assignedItem.invoiceLineItem.assignedCost + (caLineSplits?.[lineItem.lineItemID]?.nonRecoverable || 0) : 0
                        )}
                      </span>
                    {:else}
                      Not assigned
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="7" class="border-t border-gray-300"></td>
                <td class="border-t border-gray-300 px-3 py-2 text-right font-bold whitespace-nowrap">
                  Total Assigned: {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(displayTotalAssigned)}{#if caLineSplits && displayTotalAssigned !== totalAssigned} <span class="text-xs font-normal text-gray-500">(incl. PST)</span>{/if}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      {:else}
        <p class="text-red-500">No line items found for this invoice.</p>
      {/if}

      <div class="mt-4 flex justify-end">
        <div class="flex items-center gap-3">
          {#if vendorBillCreated}
            <div class="flex flex-col w-full">
              <span class="text-green-600 font-medium flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                {#if vendorBillsCreationResults.length > 1}
                  {vendorBillsCreationResults.filter(r => r.success).length}/{vendorBillsCreationResults.length} vendor bills created successfully!
                {:else}
                  Vendor bill created successfully!
                {/if}
              </span>
              
              {#if vendorBillsCreationResults.length > 0}
                <div class="w-full border rounded-md border-gray-200 overflow-hidden">
                  <table class="min-w-full divide-y divide-[#edf0f7]">
                    <thead class="bg-white">
                      <tr>
                        <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">PO Number</th>
                        <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                        <th scope="col" class="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-[#edf0f7]">
                      {#each vendorBillsCreationResults as result}
                        <tr class="{result.success ? 'bg-green-50' : 'bg-red-50'}">
                          <td class="px-3 py-2 whitespace-nowrap text-sm font-medium">
                            PO #{result.vendorOrderNumber || 'Unknown'}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-sm">
                            {#if result.success}
                              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <svg class="mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewBox="0 0 8 8">
                                  <circle cx="4" cy="4" r="3" />
                                </svg>
                                Created
                              </span>
                            {:else}
                              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                <svg class="mr-1.5 h-2 w-2 text-red-400" fill="currentColor" viewBox="0 0 8 8">
                                  <circle cx="4" cy="4" r="3" />
                                </svg>
                                Failed
                              </span>
                              {#if result.error}
                                <span class="block text-xs text-red-600 mt-1">{result.error}</span>
                              {/if}
                            {/if}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                            {#if result.success && result.url}
                              <a 
                                href={result.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                class="text-blue-600 hover:text-blue-900 text-sm"
                              >
                                View in JobTread
                              </a>
                            {/if}
                          </td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {/if}
            </div>
          {/if}
          
          {#if !vendorBillCreated}
            <button 
              on:click={nextInvoice}
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
            >
              Skip Invoice {currentInvoiceIndex + 1}/{selectedInvoices.length}
            </button>
            
            <button 
              on:click={confirmAndPrepareVendorBill}
              disabled={!allItemsAssigned || isCreatingVendorBill}
              class="font-bold py-2 px-4 rounded text-sm flex items-center 
                     {(!allItemsAssigned || isCreatingVendorBill) 
                       ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                       : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                     }
                     {isCreatingVendorBill ? 'opacity-75' : ''}"
            >
              {#if isCreatingVendorBill}
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {#if processingVendorBills}
                  Creating bills ({vendorBillsProcessed}/{totalVendorBillsToProcess})...
                {:else}
                  Creating...
                {/if}
              {:else}
                {allItemsAssigned ? 'Confirm Vendor' : 'Assign All Line Items'}
              {/if}
            </button>
          {/if}
          
          {#if vendorBillCreated}
            <button 
              on:click={nextInvoice}
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm whitespace-nowrap"
            >
              Next Invoice {currentInvoiceIndex + 1}/{selectedInvoices.length}
            </button>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Add Vendor Bill Dialog -->
    {#if showAddVendorBillDialog}
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-lg p-5 max-w-lg w-full">
          <div class="flex justify-between items-center mb-3">
            <h3 class="text-lg font-medium">Add Vendor PO</h3>
            <button 
              on:click={toggleAddVendorBillDialog}
              class="text-gray-500 hover:text-gray-700 text-xl font-bold border-0"
            >
              ×
            </button>
          </div>
        
          <div class="mb-3">
            <label for="customer-search" class="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <div class="relative">
              <input
                type="text"
                id="customer-search"
                placeholder="Type to search for a customer..."
                class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                bind:value={customerSearchQuery}
                on:input={handleCustomerSearch}
                on:focus={() => showCustomerDropdown = true}
                on:blur={() => handleInputFocusOut('customer')}
              />
              
              {#if showCustomerDropdown}
                <div class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {#if filteredCustomers.length === 0}
                    <div class="text-gray-500 px-4 py-2">No customers found</div>
                  {:else}
                    {#each filteredCustomers as customer}
                      <div 
                        class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white {customer.id === selectedCustomer ? 'bg-indigo-100' : ''}"
                        on:click={() => selectFilteredCustomer(customer.id)}
                      >
                        {customer.name}
                      </div>
                    {/each}
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        
          <div class="mb-4">
            <label for="vendor-order-search" class="block text-sm font-medium text-gray-700 mb-1">Vendor Order</label>
            <div class="relative">
              {#if isLoadingVendorOrders}
                <div class="border border-gray-300 rounded-md p-2 flex items-center justify-center">
                  <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <span class="ml-2">Loading vendor orders...</span>
                </div>
              {:else}
                <input
                  type="text"
                  id="vendor-order-search"
                  placeholder="Type to search for a vendor order..."
                  class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  bind:value={vendorOrderSearchQuery}
                  on:input={handleVendorOrderSearch}
                  on:focus={() => showVendorOrdersList()}
                  on:blur={() => handleInputFocusOut('vendorOrder')}
                />
                
                {#if showVendorOrderDropdown}
                  <div class="absolute mt-1 w-full bg-white shadow-lg z-20 max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {#if filteredVendorOrders.length === 0}
                      <div class="text-gray-500 px-4 py-2">No vendor orders found</div>
                    {:else}
                      {#each filteredVendorOrders as order}
                        <div 
                          class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white {order.id === selectedVendorOrder ? 'bg-indigo-100' : ''}"
                          on:click={() => selectFilteredVendorOrder(order.id)}
                        >
                          <div class="flex flex-col">
                            <div class="flex items-center justify-between">
                              <span class="font-medium block truncate">
                                PO #{order.number}
                              </span>
                              <span class="ml-2 text-sm text-gray-500">
                                {new Intl.NumberFormat(taxConfig.locale, { style: 'currency', currency: taxConfig.currency }).format(order.cost || 0)}
                              </span>
                            </div>
                            {#if order.subject}
                              <span class="text-sm text-gray-500 truncate">
                                {order.subject.substring(0, 50)}
                              </span>
                            {/if}
                          </div>
                        </div>
                      {/each}
                    {/if}
                  </div>
                {/if}
              {/if}
            </div>
          </div>
          
          <div class="flex justify-end gap-3">
            <button 
              on:click={toggleAddVendorBillDialog}
              class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
            >
              Cancel
            </button>
            <button 
              on:click={addVendorBill}
              disabled={!selectedCustomer || !selectedVendorOrder || isLoadingVendorOrders}
              class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50 text-sm"
            >
              Add for Matching
            </button>
          </div>
        </div>
      </div>
    {/if}

    {#if showVendorMatching && !vendorBillCreated}
      <div id="vendor-matching-section" class="mt-6 bg-gray-50 p-3 rounded-md border border-gray-200">
        <h3 class="text-lg font-medium mb-3">Match Vendor</h3>
        
        <div class="mb-3">
          <p class="text-gray-700 mb-2">
            <span class="font-medium">Xero Vendor:</span> {currentInvoice.contact?.name || 'Unknown'}
          </p>
          
          <!-- Auto-matching status messages -->
          <div class="mb-2">
            {#if isAutoMatchingVendor}
              <div class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                <span class="text-sm text-blue-700">Searching Jobtread for Vendor</span>
              </div>
            {/if}
            
            {#if autoMatchedVendor}
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span class="text-sm text-green-700">Automatically found the Vendor in Jobtread!</span>
              </div>
            {/if}
            
            {#if pendingVendorCandidates.length > 0}
              <div class="flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm text-blue-700">We found close matches — please confirm</span>
              </div>
            {:else if autoMatchFailed}
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span class="text-sm text-orange-700">We didn't find a matching Vendor choose one below</span>
              </div>
            {/if}
          </div>

          <!-- Candidate picker UI -->
          {#if pendingVendorCandidates.length > 0}
            <div class="mb-3 border border-blue-200 rounded-md bg-blue-50 p-3">
              <p class="text-sm text-gray-700 mb-2">
                Searching for: <span class="font-medium">"{pendingVendorSourceName}"</span>
              </p>
              <div class="space-y-2">
                {#each pendingVendorCandidates as candidate}
                  <div class="flex items-center justify-between bg-white rounded border border-gray-200 px-3 py-2">
                    <div>
                      <span class="text-sm font-medium text-gray-800">{candidate.name}</span>
                      <span class="text-xs text-gray-500 ml-2">Close match</span>
                    </div>
                    <button
                      class="px-3 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded"
                      on:click={() => confirmVendorCandidate(candidate, pendingVendorSourceName)}
                    >
                      Confirm
                    </button>
                  </div>
                {/each}
              </div>
              <button
                class="mt-2 px-3 py-1 text-xs font-medium bg-gray-200 hover:bg-gray-300 text-gray-700 rounded"
                on:click={rejectVendorCandidates}
              >
                None of these
              </button>
            </div>
          {/if}
          
          <!-- Manual UI - shows when no candidates and auto-match failed -->
          {#if !isAutoMatchingVendor && pendingVendorCandidates.length === 0 && (autoMatchedVendor || autoMatchFailed)}
            <div class="mb-2">
              <button 
                class="px-2 py-0.5 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 rounded inline-flex items-center"
                on:click={() => {
                  const searchPrefix = (currentInvoice.contact?.name || '').substring(0, 5);
                  vendorSearchQuery = searchPrefix;
                  autoMatchedVendor = false;
                  autoMatchFailed = false;
                  filterVendors();
                }}
              >
                Add name to search
              </button>
            </div>
          {/if}
          
          {#if pendingVendorCandidates.length === 0}
          <div class="relative">
            <input
              type="text"
              placeholder="Search for a vendor..."
              class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vendorSearchQuery}
              on:input={handleVendorSearch}
            />
            {#if isLoadingVendors}
              <div class="absolute right-3 top-2">
                <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
              </div>
            {/if}
          </div>
          {/if}
        </div>
        
        {#if pendingVendorCandidates.length === 0}
        <div class="mb-3 max-h-60 overflow-y-auto border border-gray-200 rounded-md bg-white">
          {#if filteredVendors.length === 0}
            <p class="p-3 text-gray-500 text-center">No vendors found. Try a different search term or create a new vendor.</p>
          {:else}
            <ul class="divide-y divide-[#edf0f7]">
              {#each filteredVendors.slice(0, 3) as vendor}
                <li 
                  class="p-3 hover:bg-gray-100 cursor-pointer flex items-center justify-between {selectedVendorId === vendor.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}"
                  on:click={() => selectVendor(vendor.id)}
                >
                  <span>{vendor.name}</span>
                  {#if selectedVendorId === vendor.id}
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        </div>
        
        <RequiredVendorCustomField
          fields={requiredVendorFields}
          bind:values={requiredVendorFieldValues}
          message={requiredVendorError}
        />

        <div class="flex justify-between">
          <button 
            on:click={cancelVendorMatching}
            class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
          >
            Cancel
          </button>
          
          <div class="flex gap-3">
            <button 
              on:click={createNewVendor}
              disabled={isCreatingVendor || !requiredCustomFieldsFilled(requiredVendorFields, requiredVendorFieldValues)}
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm flex items-center"
            >
              {#if isCreatingVendor}
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {/if}
              Create New Vendor from Xero Details
            </button>
            
            <button 
              on:click={createVendorBill}
              disabled={!selectedVendorId || isCreatingVendorBill}
              class="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded text-sm flex items-center {!selectedVendorId ? 'opacity-50 cursor-not-allowed' : ''}"
            >
              {#if isCreatingVendorBill}
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {/if}
              Create Vendor Bill
            </button>
          </div>
        </div>
        {/if}
      </div>
    {/if}
  </div>
</Layout>

<!-- Error Modal -->
{#if showErrorModal && creationError}
  <div class="fixed z-50 inset-0 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      
      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <!-- Exclamation icon -->
              <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Error Creating Vendor Bill
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  {creationError}
                </p>
                <p>Vendor Bill may already exist in Jobtread</p>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button 
            type="button" 
            on:click={closeErrorModal}
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .process-selected-invoices {
    padding: 16px;
  }
  .error {
    color: red;
    font-weight: bold;
  }
</style>

